// src/components/admin/UserRegionManagement.js

import React, { useState, useEffect } from 'react';
import { 
  getFirestore, 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  arrayUnion, 
  arrayRemove 
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

const UserRegionManagement = () => {
  const [users, setUsers] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  const auth = getAuth();
  const db = getFirestore();

  // Load users and regions on component mount
  useEffect(() => {
    loadUsers();
    loadRegions();
  }, []);

  const loadUsers = async () => {
    try {
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('❌ Error loading users:', error);
    }
  };

  const loadRegions = async () => {
    try {
      // You can either fetch from your API or hardcode available regions
      const response = await fetch('/api/regions');
      const regions = await response.json();
      setAvailableRegions(regions);
    } catch (error) {
      console.error('❌ Error loading regions:', error);
      // Fallback regions
      setAvailableRegions([
        { id: 'vquest', name: 'New Zealand' },
        { id: 'vquest-au-1', name: 'Australia' },
        { id: 'vquest-irl-1', name: 'Ireland' },
        { id: 'vquest-us-1', name: 'USA/Canada' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Admin function to assign regions to users
  const assignRegionToUser = async (userId, regionId) => {
    setActionLoading(prev => ({ ...prev, [`${userId}-${regionId}`]: true }));
    
    try {
      const userRef = doc(db, 'users', userId);
      const adminEmail = auth.currentUser?.email;
      
      await setDoc(userRef, {
        [`regionAccess.${regionId}`]: {
          assignedAt: new Date().toISOString(),
          assignedBy: adminEmail,
          status: 'active'
        },
        allowedRegions: arrayUnion(regionId)
      }, { merge: true });

      console.log(`✅ Assigned region ${regionId} to user ${userId}`);
      
      // Refresh users list
      await loadUsers();
      
    } catch (error) {
      console.error('❌ Error assigning region:', error);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${userId}-${regionId}`]: false }));
    }
  };

  // ✅ Admin function to remove region access
  const revokeRegionAccess = async (userId, regionId) => {
    setActionLoading(prev => ({ ...prev, [`${userId}-${regionId}-revoke`]: true }));
    
    try {
      const userRef = doc(db, 'users', userId);
      const adminEmail = auth.currentUser?.email;
      
      await setDoc(userRef, {
        [`regionAccess.${regionId}.status`]: 'revoked',
        [`regionAccess.${regionId}.revokedAt`]: new Date().toISOString(),
        [`regionAccess.${regionId}.revokedBy`]: adminEmail,
        allowedRegions: arrayRemove(regionId)
      }, { merge: true });

      console.log(`✅ Revoked region ${regionId} from user ${userId}`);
      
      // Refresh users list
      await loadUsers();
      
    } catch (error) {
      console.error('❌ Error revoking region access:', error);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${userId}-${regionId}-revoke`]: false }));
    }
  };

  if (loading) {
    return <div>Loading users and regions...</div>;
  }

  return (
    <div className="user-region-management">
      <h2>User Region Management</h2>
      
      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>User Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Current Regions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{user.email}</td>
                <td>{user.role}</td>
                <td>{user.status}</td>
                <td>
                  {user.allowedRegions?.map(regionId => {
                    const region = availableRegions.find(r => r.id === regionId);
                    return (
                      <span key={regionId} className="region-tag">
                        {region?.name || regionId}
                        <button
                          onClick={() => revokeRegionAccess(user.id, regionId)}
                          disabled={actionLoading[`${user.id}-${regionId}-revoke`]}
                          className="revoke-btn"
                        >
                          ×
                        </button>
                      </span>
                    );
                  }) || 'No regions assigned'}
                </td>
                <td>
                  <div className="region-actions">
                    {availableRegions.map(region => {
                      const hasAccess = user.allowedRegions?.includes(region.id);
                      const isLoading = actionLoading[`${user.id}-${region.id}`];
                      
                      if (hasAccess) return null;
                      
                      return (
                        <button
                          key={region.id}
                          onClick={() => assignRegionToUser(user.id, region.id)}
                          disabled={isLoading}
                          className="assign-btn"
                        >
                          {isLoading ? 'Adding...' : `Add ${region.name}`}
                        </button>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserRegionManagement;
